<template>
  <section id="objectives" class="relative overflow-hidden bg-white py-16 sm:py-24 lg:py-28">
    <div class="absolute inset-0 pointer-events-none overflow-hidden">
      <div class="absolute inset-0 bg-dot-pattern opacity-[0.03]"></div>
      <div class="absolute -top-20 -right-10 w-96 h-96 bg-gradient-to-br from-gray-900 to-black rounded-full blur-3xl mix-blend-multiply opacity-10"></div>
      <div class="absolute top-40 -left-10 w-80 h-80 bg-gradient-to-tr from-gray-800 to-black rounded-full blur-3xl mix-blend-multiply opacity-10"></div>
      <div class="absolute bottom-0 right-20 w-64 h-64 bg-gradient-to-tl from-gray-900 to-black rounded-full blur-2xl mix-blend-multiply opacity-10"></div>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center max-w-3xl mx-auto mb-6 sm:mb-20">
        <!-- Title -->
        <h2 class="text-4xl sm:text-5xl lg:text-6xl font-bold text-blue-900 mb-2 tracking-tight animate-fade-in-up delay-200">
          {{ $t('objectives.title') }} 
          <span class="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
            {{ $t('objectives.titleHighlight') }}
          </span>
        </h2>

        <!-- Description -->
        <p class="text-lg sm:text-xl text-dark-600 leading-relaxed animate-fade-in-up delay-400">
          {{ $t('objectives.description') }}
        </p>
      </div>

      <!-- Bento Grid Layout -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
        <!-- Card 1: Efficient Reporting -->
        <div class="group relative overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 p-6 sm:p-8 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-900/20 hover:border-blue-500 md:col-span-2">
          <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-black"></div>
          <div class="absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-20 bg-blue-900/30"></div>

          <div class="relative z-10 flex h-full flex-col sm:flex-row gap-5">
            <!-- Icon -->
            <div class="shrink-0 self-start rounded-xl border border-blue-800 bg-blue-900/30 p-3.5 text-blue-300 transition-transform duration-500 group-hover:scale-110 group-hover:bg-blue-900/50">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>

            <div class="relative z-10 flex flex-1 flex-col justify-between">
              <div class="text-left">
                <div class="mb-2 flex items-center gap-2">
                  <h3 class="text-xl font-bold text-white">{{ $t('objectives.card1.title') }}</h3>
                  <span class="inline-flex items-center rounded-full border border-blue-800 bg-blue-900/30 px-2.5 py-0.5 text-xs font-semibold text-blue-300 shrink-0">
                    {{ $t('objectives.card1.badge') }}
                  </span>
                </div>
                
                <p class="mb-2 text-sm text-gray-300 leading-relaxed text-left">
                  {{ $t('objectives.card1.description') }}
                </p>
              </div>

              <!-- Tags -->
              <div class="mt-2 flex flex-wrap gap-2">
                <span class="inline-flex items-center rounded-md bg-gray-800 px-2.5 py-1 text-xs font-medium text-gray-300 border border-gray-700">
                  {{ $t('objectives.card1.tag1') }}
                </span>
                <span class="inline-flex items-center rounded-md bg-gray-800 px-2.5 py-1 text-xs font-medium text-gray-300 border border-gray-700">
                  {{ $t('objectives.card1.tag2') }}
                </span>
                <span class="inline-flex items-center rounded-md bg-gray-800 px-2.5 py-1 text-xs font-medium text-gray-300 border border-gray-700">
                  {{ $t('objectives.card1.tag3') }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 2: Real-Time Alerts -->
        <div class="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 bg-gray-900 p-4 sm:p-8 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-xl hover:shadow-red-900/20 hover:border-red-500">
          <div class="absolute inset-0 bg-gradient-to-br from-gray-900 to-black"></div>
          <div class="absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-20 bg-red-900/30"></div>

          <div class="relative z-10 flex h-full flex-col sm:flex-row gap-3 sm:gap-5 items-start">
            <!-- Icon -->
            <div class="shrink-0 rounded-lg sm:rounded-xl border border-red-800 bg-red-900/30 p-2.5 sm:p-3.5 text-red-300 transition-transform duration-500 group-hover:scale-110 group-hover:bg-red-900/50">
              <svg class="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.204 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>

            <div class="relative z-10 flex-1 text-left">
              <h3 class="text-base sm:text-xl font-bold text-white mb-2">{{ $t('objectives.card2.title') }}</h3>
              
              <p class="text-xs sm:text-sm text-gray-300 leading-relaxed mb-3 text-justify sm:text-left">
                {{ $t('objectives.card2.description') }}
              </p>

              <!-- Active Status -->
              <div class="inline-flex items-center gap-1.5 rounded-full bg-green-900/30 px-2.5 py-1 text-xs font-medium text-green-300 border border-green-800">
                <span class="relative flex h-1.5 w-1.5">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                </span>
                {{ $t('objectives.card2.badge') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Card 3: Advanced Analytics -->
        <div class="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 bg-gray-900 p-4 sm:p-6 md:p-8 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-lg sm:hover:shadow-xl hover:shadow-purple-900/20 hover:border-purple-500">
          <div class="absolute inset-0 bg-gradient-to-br from-gray-900 to-black"></div>
          <div class="absolute -right-6 sm:-right-10 -top-6 sm:-top-10 h-24 w-24 sm:h-40 sm:w-40 rounded-full opacity-0 blur-2xl sm:blur-3xl transition-opacity duration-500 group-hover:opacity-20 bg-purple-900/30"></div>

          <div class="relative z-10 flex h-full items-start gap-3 sm:gap-4 md:gap-5">
            <!-- Icon -->
            <div class="shrink-0 rounded-lg sm:rounded-xl border border-purple-800 bg-purple-900/30 p-2 sm:p-3 text-purple-300 transition-transform duration-500 group-hover:scale-105 sm:group-hover:scale-110 group-hover:bg-purple-900/50">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>

            <div class="relative z-10 flex-1 text-left min-w-0">
              <h3 class="text-base sm:text-lg md:text-xl font-bold text-white mb-1.5 sm:mb-2">
                {{ $t('objectives.card3.title') }}
              </h3>
              
              <p class="text-xs sm:text-sm text-gray-300 leading-relaxed text-justify sm:text-left">
                {{ $t('objectives.card3.description') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Card 4: Enterprise Security -->
        <div class="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 bg-gray-900 p-4 sm:p-6 md:p-8 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-lg sm:hover:shadow-xl hover:shadow-green-900/20 hover:border-green-500 md:col-span-2">
          <div class="absolute inset-0 bg-gradient-to-br from-gray-900 to-black"></div>
          <div class="absolute -right-6 sm:-right-10 -top-6 sm:-top-10 h-24 w-24 sm:h-40 sm:w-40 rounded-full opacity-0 blur-2xl sm:blur-3xl transition-opacity duration-500 group-hover:opacity-20 bg-green-900/30"></div>

          <div class="relative z-10 flex h-full flex-col sm:flex-row gap-3 sm:gap-4 md:gap-5">
            <!-- Icon -->
            <div class="shrink-0 self-start rounded-lg sm:rounded-xl border border-green-800 bg-green-900/30 p-2.5 sm:p-3 md:p-3.5 text-green-300 transition-transform duration-500 group-hover:scale-105 sm:group-hover:scale-110 group-hover:bg-green-900/50">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>

            <div class="relative z-10 flex flex-1 flex-col justify-between text-left">
              <div>
                <h3 class="text-base sm:text-lg md:text-xl font-bold text-white mb-2 sm:mb-3">
                  {{ $t('objectives.card4.title') }}
                </h3>
                
                <p class="mb-3 sm:mb-4 text-xs sm:text-sm leading-relaxed text-gray-300 text-justify sm:text-left">
                  {{ $t('objectives.card4.description') }}
                </p>
              </div>

              <!-- Tags -->
              <div class="mt-3 sm:mt-auto flex flex-wrap gap-1.5 sm:gap-2">
                <span class="text-xs text-gray-400 font-medium">
                  {{ $t('objectives.card4.badge') }}
                </span>
                <span class="hidden sm:inline text-xs text-gray-600">•</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 5: Customizable Workflows -->
        <div class="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 bg-gray-900 p-4 sm:p-6 md:p-8 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-lg sm:hover:shadow-xl hover:shadow-yellow-900/20 hover:border-yellow-500">
          <div class="absolute inset-0 bg-gradient-to-br from-gray-900 to-black"></div>
          <div class="absolute -right-6 sm:-right-10 -top-6 sm:-top-10 h-24 w-24 sm:h-40 sm:w-40 rounded-full opacity-0 blur-2xl sm:blur-3xl transition-opacity duration-500 group-hover:opacity-20 bg-yellow-900/30"></div>

          <div class="relative z-10 flex h-full items-start gap-3 sm:gap-4 md:gap-5">
            <!-- Icon -->
            <div class="shrink-0 rounded-lg sm:rounded-xl border border-yellow-800 bg-yellow-900/30 p-2.5 sm:p-3 md:p-3.5 text-yellow-300 transition-transform duration-500 group-hover:scale-105 sm:group-hover:scale-110 group-hover:bg-yellow-900/50">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>

            <div class="relative z-10 flex-1 text-left min-w-0">
              <h3 class="text-base sm:text-lg md:text-xl font-bold text-white mb-1.5 sm:mb-2">
                {{ $t('objectives.card5.title') }}
              </h3>
              
              <p class="text-xs sm:text-sm text-gray-300 leading-relaxed text-justify sm:text-left">
                {{ $t('objectives.card5.description') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Card 6: Global Accessibility -->
        <div class="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-gray-800 bg-gray-900 p-4 sm:p-6 md:p-8 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-lg sm:hover:shadow-xl hover:shadow-teal-900/20 hover:border-teal-500">
          <div class="absolute inset-0 bg-gradient-to-br from-gray-900 to-black"></div>
          <div class="absolute -right-6 sm:-right-10 -top-6 sm:-top-10 h-24 w-24 sm:h-40 sm:w-40 rounded-full opacity-0 blur-2xl sm:blur-3xl transition-opacity duration-500 group-hover:opacity-20 bg-teal-900/30"></div>

          <div class="relative z-10 flex h-full items-start gap-3 sm:gap-4 md:gap-5">
            <!-- Icon -->
            <div class="shrink-0 rounded-lg sm:rounded-xl border border-teal-800 bg-teal-900/30 p-2.5 sm:p-3 md:p-3.5 text-teal-300 transition-transform duration-500 group-hover:scale-105 sm:group-hover:scale-110 group-hover:bg-teal-900/50">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <div class="relative z-10 flex-1 text-left min-w-0">
              <h3 class="text-base sm:text-lg md:text-xl font-bold text-white mb-1.5 sm:mb-2">
                {{ $t('objectives.card6.title') }}
              </h3>
              
              <p class="text-xs sm:text-sm text-gray-300 leading-relaxed text-justify sm:text-left mb-2">
                {{ $t('objectives.card6.description') }}
              </p>
              
              <div class="space-y-1.5 text-xs text-gray-400">
                <div class="flex items-start gap-1.5">
                  <span class="text-teal-400 mt-0.5">•</span>
                  <span>{{ $t('objectives.card6.bullet1') }}</span>
                </div>
                <div class="flex items-start gap-1.5">
                  <span class="text-teal-400 mt-0.5">•</span>
                  <span>{{ $t('objectives.card6.bullet2') }}</span>
                </div>
                <div class="flex items-start gap-1.5">
                  <span class="text-teal-400 mt-0.5">•</span>
                  <span>{{ $t('objectives.card6.bullet3') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ObjectivesSection',
  mounted() {
    // Add scroll animation for cards
    this.$nextTick(() => {
      const cards = document.querySelectorAll('.group');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
      
      cards.forEach(card => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';
        card.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
        observer.observe(card);
      });
    });
  }
};
</script>

<style scoped>
/* Same styles as before - no changes */
.bg-dot-pattern {
  background-image: radial-gradient(#cbd5e1 1px, transparent 1px);
  background-size: 24px 24px;
}

.animate-fade-in-up {
  animation: fadeInUp 0.8s ease-out forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.delay-200 {
  animation-delay: 200ms;
}

.delay-400 {
  animation-delay: 400ms;
}

.delay-600 {
  animation-delay: 600ms;
}

@media (max-width: 768px) {
  .bg-dot-pattern {
    background-size: 20px 20px;
  }
  
  .group {
    padding: 1.5rem !important;
  }
  
  .text-4xl {
    font-size: 2rem;
  }
  
  .text-5xl {
    font-size: 2.5rem;
  }
  
  .text-6xl {
    font-size: 3rem;
  }
}

@media (prefers-color-scheme: dark) {
  section {
    background-color: #111827;
  }
  
  .bg-white {
    background-color: #1f2937;
  }
  
  .text-gray-900 {
    color: #f9fafb;
  }
  
  .text-gray-600 {
    color: #d1d5db;
  }
  
  .border-gray-200 {
    border-color: #374151;
  }
  
  .bg-gray-100 {
    background-color: #374151;
  }
  
  .border-gray-100 {
    border-color: #4b5563;
  }
  
  .bg-blue-50 { background-color: #1e3a8a; }
  .bg-red-50 { background-color: #7f1d1d; }
  .bg-purple-50 { background-color: #4c1d95; }
  .bg-green-50 { background-color: #064e3b; }
  .bg-yellow-50 { background-color: #78350f; }
  .bg-teal-50 { background-color: #134e4a; }
  
  .border-blue-100 { border-color: #1e40af; }
  .border-red-100 { border-color: #dc2626; }
  .border-purple-100 { border-color: #7c3aed; }
  .border-green-100 { border-color: #10b981; }
  .border-yellow-100 { border-color: #f59e0b; }
  .border-teal-100 { border-color: #0d9488; }
  
  .text-blue-600 { color: #93c5fd; }
  .text-red-600 { color: #fca5a5; }
  .text-purple-600 { color: #c4b5fd; }
  .text-green-600 { color: #86efac; }
  .text-yellow-600 { color: #fde047; }
  .text-teal-600 { color: #5eead4; }
  
  .bg-dot-pattern {
    background-image: radial-gradient(#4b5563 1px, transparent 1px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .animate-fade-in-up,
  .group,
  .transition-all,
  .transition-transform,
  .transition-opacity,
  .animate-ping {
    animation: none;
    transition: none;
  }
  
  .animate-fade-in-up,
  .group {
    opacity: 1;
    transform: none;
  }
}

@media print {
  section {
    background: white !important;
    padding: 1rem !important;
  }
  
  .absolute {
    display: none;
  }
  
  .group {
    border: 1px solid #e5e7eb !important;
    box-shadow: none !important;
    page-break-inside: avoid;
  }
  
  .group:hover {
    transform: none !important;
  }
}
</style>